/**
 * variant-radios-optimized
 *
 * Isolated custom element for the products.optimized template.
 * Uses the Shopify Section Rendering API to load variant availability
 * on demand — no `product.variants | json` blob required.
 *
 * Supports:
 *   - Radio button pickers  (<variant-radios-optimized>)
 *   - Combined listings     (data-product-url per option value)
 *
 * Does NOT touch any global JS or existing custom elements.
 */

class VariantRadiosOptimized extends HTMLElement {
  constructor() {
    super();
    this._abortController = null;
    this.addEventListener('change', this.onVariantChange.bind(this));
  }

  connectedCallback() {
    // Set initial button state from the server-rendered data attributes.
    this.syncButtonState();
  }

  // ─── Getters ───────────────────────────────────────────────────────────────

  get sectionId() {
    return this.dataset.section ?? '';
  }

  get productUrl() {
    return this.dataset.url ?? '';
  }

  get selectedVariantAvailable() {
    return this.dataset.selectedAvailable !== 'false';
  }

  // ─── Event handler ─────────────────────────────────────────────────────────

  onVariantChange(event) {
    const input = event.target;
    if (!input.matches('input[type="radio"]')) return;

    // Combined listings: the changed input may point to a different product.
    const targetProductUrl = input.dataset.productUrl || this.productUrl;

    // Collect all checked option-value IDs across all fieldsets.
    const optionValueIds = Array.from(
      this.querySelectorAll('input[type="radio"]:checked[data-option-value-id]')
    )
      .map((el) => el.dataset.optionValueId)
      .filter(Boolean);

    const params =
      optionValueIds.length > 0
        ? `option_values=${optionValueIds.join(',')}`
        : '';

    this.setButtonState('loading');
    this.clearFormErrors();
    this.fetchSection(targetProductUrl, params, input.id);
  }

  // ─── Section fetch ─────────────────────────────────────────────────────────

  fetchSection(productUrl, params, focusInputId) {
    // Cancel any previous in-flight request.
    if (this._abortController) this._abortController.abort();
    this._abortController = new AbortController();

    const url = `${productUrl}?section_id=${this.sectionId}&${params}`;

    fetch(url, { signal: this._abortController.signal })
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        this.applyResponse(doc, focusInputId, params);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        console.error('[variant-radios-optimized] fetch error:', err);
        this.syncButtonState();
      });
  }

  // ─── DOM updates after fetch ───────────────────────────────────────────────

  applyResponse(doc, focusInputId, params) {
    this.updatePickerHTML(doc);
    this.updatePriceElements(doc);
    this.updateDiscountMessage(doc);
    this.updateVariantInput(doc);
    this.updateCustomProperties(doc);
    this.updatePreSale(doc);
    this.updateSelectedOptions(doc);
    const nextUrl = this.updateURL();
    this.updateApBuyBox(doc);
    this.updateUpsellBundle(doc);
    this.syncButtonState();

    // Dispatch global events that standard theme components listen to
    this.dispatchGlobalEvents(nextUrl);

    if (focusInputId) {
      document.getElementById(focusInputId)?.focus();
    }
  }

  /**
   * Replace the picker's children with the freshly rendered version.
   * We only swap innerHTML so the custom element node itself stays in the DOM
   * (preserving the change event listener attached in the constructor).
   */
  updatePickerHTML(doc) {
    const newPicker = doc.querySelector('variant-radios-optimized');
    if (!newPicker) return;

    for (const attr of newPicker.attributes) {
      if (attr.name.startsWith('data-')) {
        this.setAttribute(attr.name, attr.value);
      }
    }

    this.innerHTML = newPicker.innerHTML;
  }

  updatePriceElements(doc) {
    const sid = this.sectionId;
    this.swapInnerHTML(`[data-price="${sid}"]`, doc);
    this.swapInnerHTML(`#price-${sid}`, doc);
  }

  updateDiscountMessage(doc) {
    const sid = this.sectionId;
    this.swapInnerHTML(`[data-discount-msg="${sid}"]`, doc);
    this.swapInnerHTML(`[data-discount-msg-test="${sid}"]`, doc);

    const discountEl = document.querySelector(`[data-discount-msg="${sid}"]`);
    const buyBorderEl = document.querySelector('[data-buy-border]');
    if (discountEl && buyBorderEl) {
      buyBorderEl.dataset.buyBorder =
        discountEl.innerHTML.trim() !== '' ? 'true' : 'false';
    }
  }

  updateVariantInput(doc) {
    const sid = this.sectionId;
    const selectors = [
      `#product-form-${sid} input[name="id"]`,
      `#product-form-installment-${sid} input[name="id"]`,
    ];
    const newInput = doc.querySelector(`#product-form-${sid} input[name="id"]`);
    if (!newInput) return;

    selectors.forEach((sel) => {
      const el = document.querySelector(sel);
      if (el) {
        el.value = newInput.value;
        // setAttribute triggers MutationObserver watchers (e.g. promo-upsell-dropdown)
        // which observe attributes — setting .value alone is invisible to MutationObserver.
        el.setAttribute('value', newInput.value);
        el.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  updateCustomProperties(doc) {
    const sid = this.sectionId;
    this.swapInnerHTML(`[data-custom-properties="${sid}"]`, doc);
  }

  updatePreSale(doc) {
    const sid = this.sectionId;
    this.swapInnerHTML(`[data-pre-sale="${sid}"]`, doc);
  }

  updateSelectedOptions(doc) {
    // The snippet renders [data-option-selected] on the `<legend>` or `<span>` 
    // to show exactly what's currently picked. We can swap them directly.
    const container = document.querySelector(`variant-radios-optimized[data-section="${this.sectionId}"]`);
    if (!container) return;

    container.querySelectorAll('[data-option-selected]').forEach((el, index) => {
      // Since there can be multiple (Color, Size, etc.), we match by index 
      // within the newly rendered picker doc.
      const newContainer = doc.querySelector(`variant-radios-optimized[data-section="${this.sectionId}"]`);
      if (newContainer) {
        const newEls = newContainer.querySelectorAll('[data-option-selected]');
        if (newEls[index]) {
          el.innerHTML = newEls[index].innerHTML;
        }
      }
    });
  }

  /**
   * Fire the event that ap-buy-box listens to so it shows/hides
   * when the selected variant is sold out.
   * ap-buy-box watches: #MainContent → 'theme:section:apbuybox:update'
   * with detail: { variant: { available: boolean } }
   */
  updateApBuyBox(doc) {
    const mainContent = document.querySelector('#MainContent');
    if (!mainContent) return;

    // Read availability from the freshly-fetched section HTML.
    // The picker element carries data-selected-available after updatePickerHTML.
    const available = this.selectedVariantAvailable;
    const variantId = this.dataset.selectedVariantId;

    mainContent.dispatchEvent(
      new CustomEvent('theme:section:apbuybox:update', {
        bubbles: true,
        detail: { variant: { available, id: variantId } },
      })
    );
  }

  /**
   * Replace the entire upsell bundle slot. This ensures that when a
   * variant is sold out, the bundle wrapper is correctly hidden, matching
   * the original section render behavior.
   */
  updateUpsellBundle(doc) {
    this.swapInnerHTML('[upsell-bundle-slot]', doc);
    this.swapInnerHTML('[data-points]', doc);
  }

  /**
   * Update the browser URL to reflect the newly selected variant.
   * Returns the built URL so callers can pass it along (e.g. to the gallery).
   */
  updateURL() {
    const variantId = this.dataset.selectedVariantId;
    if (!variantId || !this.productUrl) return null;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    if (
      this.dataset.url &&
      url.pathname !== new URL(this.dataset.url, window.location.origin).pathname
    ) {
      url.pathname = new URL(this.dataset.url, window.location.origin).pathname;
    }
    window.history.replaceState({}, '', url.toString());
    return url;
  }

  /**
   * Dispatch global variant change events that components like
   * product-gallery, more-color-products, and product-description rely on,
   * keeping us isolated but maintaining feature parity.
   */
  dispatchGlobalEvents(nextUrl) {
    const mainContent = document.querySelector('#MainContent');
    if (!mainContent) return;

    const variantId = this.dataset.selectedVariantId;
    if (!variantId) return;

    // Simulate the variant object shape expected by listeners
    const mockVariant = {
      id: variantId,
      Id: variantId, // Bug in original product-gallery expects capital I
      available: this.selectedVariantAvailable
    };

    mainContent.dispatchEvent(
      new CustomEvent('theme:variant:change', {
        detail: { variant: mockVariant, url: nextUrl },
      })
    );

    mainContent.dispatchEvent(
      new CustomEvent('theme:sections:update', {
        detail: { status: 'success' },
      })
    );
  }

  // ─── Button & Form State ───────────────────────────────────────────────────

  syncButtonState() {
    this.setButtonState(this.selectedVariantAvailable ? 'active' : 'soldOut');
  }

  setButtonState(state) {
    const productForm = document.querySelector('product-form');
    productForm?.setButtonState?.(state);
  }

  clearFormErrors() {
    const productForm = document.querySelector('product-form');
    if (productForm && typeof productForm.handleErrorMessage === 'function') {
      productForm.handleErrorMessage();
    }
  }

  // ─── Utility ───────────────────────────────────────────────────────────────

  swapInnerHTML(selector, doc) {
    const current = document.querySelector(selector);
    const next = doc.querySelector(selector);
    if (current && next) current.innerHTML = next.innerHTML;
  }
}

customElements.define('variant-radios-optimized', VariantRadiosOptimized);
